import { useState, useMemo } from 'react'
import Link from 'next/link'
import { Cell, PieChart, Pie, Legend, Tooltip, ComposedChart, ResponsiveContainer, Bar, Line, CartesianGrid, XAxis, YAxis } from 'recharts'
import Head from 'next/head'
import { useRouter } from 'next/router'
import Layout from 'components/Layout'
import Statistic from '@byma/shared/components/Statistic'
import { money } from '@byma/shared/helpers/formatters'
import SalesChart from 'components/SalesChart'
import DateFilter from '@byma/shared/components/DateFilter'
import ChannelFilter from '@byma/shared/components/ChannelFilter'
import CouponFilter from '@byma/shared/components/CouponFilter'
import MarketingReport from 'components/MarketingReport'
import EventFilter from '@byma/shared/components/EventFilter'
import Tabs from '@byma/shared/components/Tabs'
import Loader from '@byma/shared/components/Loader'
import Gauge from '@byma/shared/components/Gauge'
import useAuth from '@byma/shared/hooks/useAuth'
import useFilters from '@byma/shared/hooks/useFilters'
import fetcher from '@byma/shared/helpers/fetcher'
import useSWR from 'swr'
import dayjs from 'dayjs'
import { FiShoppingBag, FiDollarSign, FiCalendar } from 'react-icons/fi'


function reportSum(report, key) {
    if (!report) return 0
    return report.reduce((acc, cur) => acc += cur[key], 0)
}

function getDaysArray(s,e) {for(var a=[],d=new Date(s);d<=e;d.setDate(d.getDate()+1)){ a.push(new Date(d));}return a;}

export default function Report() {
    const session = useAuth()
    const { token, user: currentUser } = session
    const { query, ...router } = useRouter()
    const { noSearchQueryString, parsedFilters, queryString, rawFilters } = useFilters(query)
    const { data: marketingReport } = useSWR(token ? [`/api/marketing/report?${queryString}`, null, token] : null, fetcher, { revalidateOnFocus: false })
    const { data: paymentReport } = useSWR(token ? [`/api/payments/report?${queryString}`, null, token] : null, fetcher, { revalidateOnFocus: false })
    const { data: preleads } = useSWR(token ? [`/api/preleads/getAll?${queryString}&perPage=10`, null, token] : null, fetcher, { revalidateOnFocus: false })
    const { data: shoppingCarts } = useSWR(token ? [`/api/shoppingcarts/getAll?${queryString}&perPage=10`, null, token] : null, fetcher, { revalidateOnFocus: false })

    const [dateFilterOpen, setDateFilterOpen] = useState(false);

    function getReport(key) {
        const joined = marketingReport?.result.reduce((acc, cur) => {
            let item = acc[cur?._id[key]] || {
                count: 0,
                total: 0,
            }
            acc[cur?._id[key]] = {
                name: cur?._id[key] || 'N/A',
                total: item.total + cur.total,
                count: item.count + cur.count,
            }
            return acc
        }, {})
        if (!joined) return null
        return Object.values(joined)
    }

    const tabs = [
        { title: 'Campanhas', content: <MarketingReport {...{marketingReport: getReport('utm_campaign')}} label="Campanha" /> },
        { title: 'Fontes', content: <MarketingReport {...{marketingReport: getReport('utm_source')}} label="Fonte" /> },
        { title: 'Termos', content: <MarketingReport {...{marketingReport: getReport('utm_term')}} label="Termo" /> },
        { title: 'Mídias', content: <MarketingReport {...{marketingReport: getReport('utm_medium')}} label="Mídia" /> },
    ] 

    const campaignsReport = useMemo(() => {
        if (marketingReport?.result) {
            return getReport('utm_campaign')
        }
        return null
    }, [marketingReport])

    const mediumsReport = useMemo(() => {
        if (marketingReport?.result) {
            return getReport('utm_medium')
        }
        return null
    }, [marketingReport])

    const sourcesReport = useMemo(() => {
        if (marketingReport?.result) {
            return getReport('utm_source')
        }
        return null
    }, [marketingReport])

    const termsReport = useMemo(() => {
        if (marketingReport?.result) {
            return getReport('utm_term')
        }
        return null
    }, [marketingReport])

    return (
        <Layout {...{session}}>
            <div className="p-3">
                <div className="flex space-x-3 mb-6 overflow-y-auto py-3">
                    <DateFilter activeKey={rawFilters.dateKey} />
                    <EventFilter token={token} filteredEvents={rawFilters.parties} />
                    <ChannelFilter token={token} filteredChannels={rawFilters.channels} />
                    <CouponFilter token={token} filteredCoupons={rawFilters.coupons} />
                    <Link href={`/report?${noSearchQueryString}`}>
                        <a>
                            <button>
                                <div className="flex justify-between items-center cursor-pointer rounded shadow px-2 py-1 text-sm hover:shadow-md whitespace-nowrap">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 mr-1">
                                        <path d="M15.5 2A1.5 1.5 0 0014 3.5v13a1.5 1.5 0 001.5 1.5h1a1.5 1.5 0 001.5-1.5v-13A1.5 1.5 0 0016.5 2h-1zM9.5 6A1.5 1.5 0 008 7.5v9A1.5 1.5 0 009.5 18h1a1.5 1.5 0 001.5-1.5v-9A1.5 1.5 0 0010.5 6h-1zM3.5 10A1.5 1.5 0 002 11.5v5A1.5 1.5 0 003.5 18h1A1.5 1.5 0 006 16.5v-5A1.5 1.5 0 004.5 10h-1z" />
                                    </svg>


                                    <p>
                                        Relatório Geral
                                    </p>
                                </div>
                            </button>
                        </a>
                    </Link>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                        <Statistic 
                            title="Leads cadastrados"
                            value={preleads?.count}
                            loading={!preleads}
                        />
                    </div>
                    <div>
                        <Statistic 
                            title="Carrinhos abertos"
                            value={shoppingCarts?.count}
                            loading={!shoppingCarts}
                        />
                    </div>
                    <div>
                        <Statistic 
                            title="Vendas"
                            value={reportSum(paymentReport?.result, 'count')}
                            loading={!paymentReport}
                        />
                    </div>
                </div>
                {/*
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div className="flex my-3 h-64 w-full flex-col">
                        <p className="text-2xl font-bold">Campanhas</p>
                        <ResponsiveContainer>
                            <PieChart width={730} height={250}>
                                <Pie data={campaignsReport} dataKey="count" nameKey="name" cx="50%" cy="50%"  innerRadius={60} outerRadius={80} fill="#82ca9d" label/>
                                <Tooltip />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="flex my-3 h-64 w-full flex-col">
                        <p className="text-2xl font-bold">Fontes</p>
                        <ResponsiveContainer>
                            <PieChart width={730} height={250}>
                                <Pie data={sourcesReport} dataKey="count" nameKey="campaign" cx="50%" cy="50%"  innerRadius={60} outerRadius={80} fill="#82ca9d" label/>
                                <Tooltip />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="flex my-3 h-64 w-full flex-col">
                        <p className="text-2xl font-bold">Termos</p>
                        <ResponsiveContainer>
                            <PieChart width={730} height={250}>
                                <Pie data={termsReport} dataKey="count" nameKey="campaign" cx="50%" cy="50%"  innerRadius={60} outerRadius={80} fill="#82ca9d" label/>
                                <Tooltip />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="flex my-3 h-64 w-full flex-col">
                        <p className="text-2xl font-bold">Mídias</p>
                        <ResponsiveContainer>
                            <PieChart width={730} height={250}>
                                <Pie data={mediumsReport} dataKey="count" nameKey="campaign" cx="50%" cy="50%"  innerRadius={60} outerRadius={80} fill="#82ca9d" label/>
                                <Tooltip />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div> 
                */}
                <div className="flex"> 
                    <Tabs tabs={tabs} />
                </div>
            </div>
        </Layout>
    )
}

